import styled from 'styled-components'
import { rem } from 'polished'
import { boxShadowAppear, boxShadowDisappear } from 'tripPlanner/components/DragAndDrop/styles'
import { mediaQueryUp } from 'components/utils/breakpoint'

const PinArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const BaseDetailCardContainer = styled.div`
  display: grid;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.L};
  flex: 1;

  .drag-overlay & {
    animation: ${boxShadowAppear} 0.2s forwards ease-out;
  }

  .drag-overlay.dropped & {
    animation: ${boxShadowDisappear} 0.15s forwards ease-out;
  }

  ${mediaQueryUp.desktop} {
    &:hover {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }
`

const StandardDetailCardContainer = styled(BaseDetailCardContainer)`
  grid-template-areas:
    "image content"
    "comment comment";
  grid-template-rows: auto auto;
  grid-template-columns: auto 1fr;

  @container (min-width: 640px) {
    grid-template-rows: minmax(${rem(116)}, auto) auto;
  }
`

const ImageArea = styled.div`
  grid-area: image;
  position: relative;
  align-self: stretch;
  width: ${rem(68)};
  min-height: ${rem(80)};
  padding: ${rem(12)} 0 ${rem(12)} ${rem(12)};

  &:empty {
    display: none;
  }

  @container (min-width: 640px) {
    display: initial;
    width: ${rem(180)};
    height: 100%;
    padding: 0;
  }
`

const ContentArea = styled.div`
  grid-area: content;
  padding: ${rem(12)};

  @container (min-width: 640px) {
    padding: ${rem(16)} ${rem(20)};
  }
`

const CommentArea = styled.div`
  grid-area: comment;
  padding: ${rem(8)} ${rem(12)};
  display: flex;
  align-items: center;
  overflow: hidden;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
`

export const DetailCardLayout = StandardDetailCardContainer as typeof StandardDetailCardContainer & {
  Image: typeof ImageArea
  Content: typeof ContentArea
  Comment: typeof CommentArea
}

DetailCardLayout.Image = ImageArea
DetailCardLayout.Content = ContentArea
DetailCardLayout.Comment = CommentArea

const TransportDetailCardContainer = styled(BaseDetailCardContainer)`
  grid-template-areas:
    "image content"
    "timeline timeline"
    "comment comment";
  grid-template-rows: 1fr auto auto;
  grid-template-columns: auto 1fr;
`

const TransportImageArea = styled(ImageArea)`
  width: ${rem(68)};
  min-height: ${rem(68)};
  padding: ${rem(12)} 0 ${rem(12)} ${rem(12)};

  @container (min-width: 640px) {
    width: ${rem(76)};
    min-height: ${rem(68)};
    padding: ${rem(16)} 0 ${rem(16)} ${rem(20)};
  }

  > * {
    border-radius: ${props => props.theme.borderRadius.M};
  }
`

const TimelineContainer = styled.div`
  grid-area: timeline;
  clear: both;
  padding: 0 ${rem(12)} ${rem(12)} ${rem(12)};

  @container (min-width: 640px) {
    padding: 0 ${rem(20)} ${rem(16)} ${rem(20)};
  }
`

export const TransportCardLayout = TransportDetailCardContainer as typeof TransportDetailCardContainer & {
  Image: typeof TransportImageArea
  Content: typeof ContentArea
  Comment: typeof CommentArea
  Timeline: typeof TimelineContainer
}

TransportCardLayout.Image = TransportImageArea
TransportCardLayout.Content = ContentArea
TransportCardLayout.Comment = CommentArea
TransportCardLayout.Timeline = TimelineContainer

const DetailNoteCardContainer = styled(BaseDetailCardContainer)`
  grid-template-areas:
    "content"
    "comment";
  grid-template-rows: auto auto;
  grid-template-columns: 1fr;

  &.focus {
    background-color: ${(props) => props.theme.palette.neutral.default.seven};
  }
`

const DetailNoteContentArea = styled(ContentArea)`
  grid-area: content;
  padding: ${rem(8)} ${rem(12)};

  @container (min-width: 640px) {
    padding: ${rem(12)} ${rem(20)};
  }
`

export const DetailNoteCardLayout = DetailNoteCardContainer as typeof DetailNoteCardContainer & {
  Content: typeof DetailNoteContentArea
  Comment: typeof CommentArea
}

DetailNoteCardLayout.Content = DetailNoteContentArea
DetailNoteCardLayout.Comment = CommentArea

const DetailCardAndPinLayoutBase = styled.div`
  display: flex;
  background: transparent;
  flex-direction: row;
  align-items: center;
  margin-top: ${rem(12)};
  gap: ${rem(4)};

  @container (min-width: 640px) {
    margin-top: ${rem(16)};
    gap: ${rem(8)};
  }
`

export const DetailCardAndPinLayout = DetailCardAndPinLayoutBase as typeof DetailCardAndPinLayoutBase & {
  Pin: typeof PinArea
  CardContainer: typeof StandardDetailCardContainer
}

DetailCardAndPinLayout.Pin = PinArea
DetailCardAndPinLayout.CardContainer = StandardDetailCardContainer
